import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, Input, Button, Modal, Descriptions, Table, message } from 'antd';
import {
  CarOutlined,
  CheckCircleOutlined,
  SolutionOutlined,
  SearchOutlined,
  UserOutlined,
  CalendarOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchServiceOrders,
  fetchServiceOrder,
  searchCustomerVehicles,
  type ServiceOrderDto,
  type ServiceOrderLineDto,
  type CustomerVehicleDto,
} from '../services/serviceOrderService';
import { fetchAppointments, type ServiceAppointmentDto } from '../services/appointmentService';
import dayjs from 'dayjs';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [servistekiAraclar, setServistekiAraclar] = useState<ServiceOrderDto[]>([]);
  const [tamamlananlar, setTamamlananlar] = useState<ServiceOrderDto[]>([]);
  const [teslimEdilenler, setTeslimEdilenler] = useState<ServiceOrderDto[]>([]);
  const [plateInput, setPlateInput] = useState('');
  const [plateSearching, setPlateSearching] = useState(false);
  const [plateResultModalVisible, setPlateResultModalVisible] = useState(false);
  const [plateResult, setPlateResult] = useState<CustomerVehicleDto | null>(null);
  const [acikRandevular, setAcikRandevular] = useState<ServiceAppointmentDto[]>([]);
  const [teslimSummaryVisible, setTeslimSummaryVisible] = useState(false);
  const [teslimSummaryLoading, setTeslimSummaryLoading] = useState(false);
  const [teslimSummaryOrder, setTeslimSummaryOrder] = useState<ServiceOrderDto | null>(null);

  useEffect(() => {
    loadServiceOrders();
  }, []);

  const loadServiceOrders = async () => {
    try {
      const [servisteki, tamamlanan, teslim, randevular] = await Promise.all([
        fetchServiceOrders({ onlyOpen: true }), // Kabul, Ön Tespit, Onarımda
        fetchServiceOrders({ status: 4 }),      // Tamamlandı
        fetchServiceOrders({
          status: 5,
          deliveredAtDate: dayjs().format('YYYY-MM-DD')
        }), // Bugün teslim edilenler
        fetchAppointments({
          fromDate: dayjs().subtract(7, 'day').startOf('day').toISOString(),
          toDate: dayjs().add(30, 'day').endOf('day').toISOString()
        })
      ]);
      setServistekiAraclar(servisteki);
      setTamamlananlar(tamamlanan);
      setTeslimEdilenler(teslim);
      // Açık randevular: Pending(1) veya Confirmed(2), bugünden itibaren
      const acik = randevular.filter((r: ServiceAppointmentDto) => (r.status === 1 || r.status === 2) && !r.serviceOrderId);
      setAcikRandevular(acik);
    } catch (error) {
      console.error('Service orders loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlateSearch = async () => {
    const plate = plateInput?.trim().toUpperCase();
    if (!plate) return;
    setPlateSearching(true);
    setPlateResult(null);
    setPlateResultModalVisible(false);
    try {
      const list = await searchCustomerVehicles({ plate });
      if (list && list.length > 0) {
        setPlateResult(list[0]);
        setPlateResultModalVisible(true);
      } else {
        navigate(`/add-vehicle-with-customer?plate=${encodeURIComponent(plate)}`);
      }
    } catch (err) {
      console.error('Plaka arama hatası:', err);
    } finally {
      setPlateSearching(false);
    }
  };

  const handlePlateConfirmAndGo = () => {
    if (!plateResult) return;
    setPlateResultModalVisible(false);
    navigate(`/service-customers?customerId=${plateResult.customerId}&vehicleId=${plateResult.id}`);
    setPlateResult(null);
    setPlateInput('');
  };

  const getDaysInService = (orderDate: string) => {
    const start = dayjs(orderDate).startOf('day');
    const today = dayjs().startOf('day');
    const days = today.diff(start, 'day');
    if (days === 0) return 'Bugün';
    if (days === 1) return '1 gündür';
    return `${days} gündür`;
  };

  const lineDescription = (line: ServiceOrderLineDto) => {
    if (line.itemType === 1) return line.serviceItemName || '—';
    if (line.itemType === 2) return [line.productCode, line.productName].filter(Boolean).join(' — ') || '—';
    if (line.itemType === 3) return line.externalServiceItemName || '—';
    return '—';
  };

  const openTeslimSummary = async (row: ServiceOrderDto) => {
    setTeslimSummaryVisible(true);
    setTeslimSummaryLoading(true);
    setTeslimSummaryOrder(null);
    try {
      const full = await fetchServiceOrder(row.id);
      setTeslimSummaryOrder(full);
    } catch {
      message.error('Fiş özeti yüklenemedi');
      setTeslimSummaryVisible(false);
    } finally {
      setTeslimSummaryLoading(false);
    }
  };

  const closeTeslimSummary = () => {
    setTeslimSummaryVisible(false);
    setTeslimSummaryOrder(null);
  };

  const getMiddleLabel = (row: ServiceOrderDto) => {
    if (row.deliveredAt && row.orderDate) {
      const orderDt = dayjs(row.orderDate);
      const deliveredDt = dayjs(row.deliveredAt);
      const totalMins = deliveredDt.diff(orderDt, 'minute');
      const days = deliveredDt.diff(orderDt, 'day');
      // 24 saatten az süre: saat/dakika cinsinden göster (farklı günlerde bile 0 gün yerine)
      if (days === 0) {
        const hours = Math.floor(totalMins / 60);
        const mins = totalMins % 60;
        if (hours > 0 && mins > 0) return `${hours} saat ${mins} dk sonra teslim`;
        if (hours > 0) return `${hours} saat sonra teslim`;
        return `${mins} dk sonra teslim`;
      }
      if (days === 1) return '1 gün sonra teslim';
      return `${days} gün sonra teslim`;
    }
    if (row.status === 5 && row.deliveredAt) return '-';
    return getDaysInService(row.orderDate);
  };

  const OrderItemContent = ({ row }: { row: ServiceOrderDto }) => {
    const orderDt = row.orderDate ? dayjs(row.orderDate) : null;
    const orderDateStr = orderDt ? orderDt.format('DD.MM.YYYY HH:mm') : '-';
    const middleStr = getMiddleLabel(row);
    const deliveredAtStr = row.deliveredAt
      ? `Teslim: ${dayjs(row.deliveredAt).format('DD.MM.YYYY HH:mm')}`
      : null;
    return (
      <div className="dashboard-order-item">
        <div className="order-row1">
          <span className="order-fis-no">{row.orderNo}</span>
          <span className="order-date">{orderDateStr}</span>
          <span className="order-days">({middleStr})</span>
          {deliveredAtStr && <span className="order-delivered">{deliveredAtStr}</span>}
          <span className="order-status">{row.statusText}</span>
        </div>
        <div className="order-row-vehicle">
          <span className="order-plaka">{row.vehiclePlate}</span>
          {row.vehicleInfo && <span className="order-vehicle-info">{row.vehicleInfo}</span>}
        </div>
        <div className="order-row2">
          <span className="order-customer">{row.customerName}</span>
          {row.customerPhone && <span className="order-phone">{row.customerPhone}</span>}
        </div>
      </div>
    );
  };

  const renderOrderItem = (row: ServiceOrderDto) => (
    <Link key={row.id} to={`/service-orders/${row.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <OrderItemContent row={row} />
    </Link>
  );

  const renderTeslimOrderItem = (row: ServiceOrderDto) => (
    <div
      key={row.id}
      role="button"
      tabIndex={0}
      className="dashboard-order-item dashboard-teslim-order-clickable"
      style={{ cursor: 'pointer' }}
      onClick={() => openTeslimSummary(row)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openTeslimSummary(row);
        }
      }}
    >
      <OrderItemContent row={row} />
    </div>
  );

  const OrderList = ({ list }: { list: ServiceOrderDto[] }) => (
    <div className="dashboard-order-list">
      {list.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>Kayıt yok</div>
      ) : (
        list.map(renderOrderItem)
      )}
    </div>
  );

  const TeslimOrderList = ({ list }: { list: ServiceOrderDto[] }) => (
    <div className="dashboard-order-list">
      {list.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>Kayıt yok</div>
      ) : (
        list.map(renderTeslimOrderItem)
      )}
    </div>
  );

  const renderAppointmentItem = (r: ServiceAppointmentDto) => {
    const createdAtStr = r.createdAt ? dayjs(r.createdAt).format('DD.MM.YYYY HH:mm') : '-';
    const appointmentDateStr = r.appointmentDate ? dayjs(r.appointmentDate).format('DD.MM.YYYY HH:mm') : '-';
    return (
      <Link key={r.id} to={`/ajanda`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <div className="dashboard-order-item dashboard-appointment-item">
          <div className="order-row1">
            <span className="order-date">Oluşturma: {createdAtStr}</span>
            <span className="order-status">{r.statusText}</span>
          </div>
          <div className="order-row1">
            <span className="order-date">Randevu: {appointmentDateStr}</span>
          </div>
          <div className="order-row2">
            <span className="order-customer">{r.customerName}</span>
            {r.customerPhone && (
              <span className="order-phone">
                <PhoneOutlined style={{ marginRight: 4 }} />
                {r.customerPhone}
              </span>
            )}
          </div>
          {r.vehiclePlate && (
            <div className="order-row-vehicle">
              <span className="order-plaka">{r.vehiclePlate}</span>
              {r.vehicleInfo && <span className="order-vehicle-info">{r.vehicleInfo}</span>}
            </div>
          )}
        </div>
      </Link>
    );
  };

  const AppointmentList = ({ list }: { list: ServiceAppointmentDto[] }) => (
    <div className="dashboard-order-list">
      {list.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>Açık randevu yok</div>
      ) : (
        list.map(renderAppointmentItem)
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      
      <Row gutter={[16, 10]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card size="small" title="Plaka ile Ara">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Input
                placeholder="Plaka girin (örn: 34ABC123)"
                value={plateInput}
                onChange={(e) => setPlateInput(e.target.value)}
                onPressEnter={handlePlateSearch}
                style={{ minWidth: 120, flex: '1 1 200px', maxWidth: 320 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={plateSearching}
                onClick={handlePlateSearch}
              >
                Ara
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 10]}>
        <Col xs={24} md={12}>
          <Card
            title={
              <span>
                <CarOutlined style={{ marginRight: 8 }} />
                Servisteki Araçlar
              </span>
            }
            extra={<Link to="/service-orders">Tümü</Link>}
          >
            <OrderList list={servistekiAraclar} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <span>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Tamamlananlar
              </span>
            }
            extra={<Link to="/service-orders">Tümü</Link>}
          >
            <OrderList list={tamamlananlar} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <span>
                <SolutionOutlined style={{ marginRight: 8 }} />
                Bugün Teslim Edilenler
              </span>
            }
            extra={<Link to="/service-orders">Tümü</Link>}
          >
            <TeslimOrderList list={teslimEdilenler} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <span>
                <CalendarOutlined style={{ marginRight: 8 }} />
                Açık Randevular
              </span>
            }
            extra={<Link to="/ajanda">Tümü</Link>}
          >
            <AppointmentList list={acikRandevular} />
          </Card>
        </Col>
      </Row>

      <Modal
        title={teslimSummaryOrder ? `Özet — ${teslimSummaryOrder.orderNo}` : 'Bugün teslim — özet'}
        open={teslimSummaryVisible}
        onCancel={closeTeslimSummary}
        footer={[
          <Button key="close" type="primary" onClick={closeTeslimSummary}>
            Kapat
          </Button>,
        ]}
        width={720}
        destroyOnClose
      >
        {teslimSummaryLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : teslimSummaryOrder ? (
          <div>
            <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Müşteri">{teslimSummaryOrder.customerName}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{teslimSummaryOrder.customerPhone || '—'}</Descriptions.Item>
              <Descriptions.Item label="Plaka">{teslimSummaryOrder.vehiclePlate}</Descriptions.Item>
              <Descriptions.Item label="Araç">{teslimSummaryOrder.vehicleInfo || '—'}</Descriptions.Item>
              <Descriptions.Item label="Fiş tarihi">
                {teslimSummaryOrder.orderDate ? dayjs(teslimSummaryOrder.orderDate).format('DD.MM.YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Teslim">
                {teslimSummaryOrder.deliveredAt
                  ? dayjs(teslimSummaryOrder.deliveredAt).format('DD.MM.YYYY HH:mm')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Teslim eden">
                {teslimSummaryOrder.deliveredByUserName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Sorumlu">
                {teslimSummaryOrder.responsibleUserName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Giriş / Çıkış km">
                {[teslimSummaryOrder.entryKm, teslimSummaryOrder.exitKm]
                  .map((k) => (k != null ? String(k) : null))
                  .filter(Boolean)
                  .join(' → ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">{teslimSummaryOrder.statusText}</Descriptions.Item>
              <Descriptions.Item label="Toplam (KDV dahil)">
                {Number(teslimSummaryOrder.totalAmount).toLocaleString('tr-TR', {
                  style: 'currency',
                  currency: 'TRY',
                })}
              </Descriptions.Item>
            </Descriptions>
            {(teslimSummaryOrder.complaintItems?.length ?? 0) > 0 && (
              <p style={{ marginBottom: 12 }}>
                <strong>Şikayetler:</strong>{' '}
                {teslimSummaryOrder.complaintItems!.map((c) => c.name).filter(Boolean).join(', ')}
              </p>
            )}
            {teslimSummaryOrder.notes ? (
              <p style={{ marginBottom: 12 }}>
                <strong>Not:</strong> {teslimSummaryOrder.notes}
              </p>
            ) : null}
            <Table<ServiceOrderLineDto>
              size="small"
              pagination={false}
              rowKey={(r) => String(r.id)}
              dataSource={teslimSummaryOrder.lines ?? []}
              columns={[
                { title: '#', dataIndex: 'lineNo', key: 'lineNo', width: 44 },
                { title: 'Tür', dataIndex: 'itemTypeText', key: 'itemTypeText', width: 88, ellipsis: true },
                {
                  title: 'Kalem',
                  key: 'desc',
                  ellipsis: true,
                  render: (_: unknown, r) => lineDescription(r),
                },
                { title: 'Miktar', dataIndex: 'quantity', key: 'quantity', width: 72, align: 'right' as const },
                {
                  title: 'Tutar',
                  key: 'total',
                  width: 100,
                  align: 'right' as const,
                  render: (_: unknown, r) =>
                    Number(r.totalPrice).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }),
                },
              ]}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        title="Plaka kayıt bilgisi"
        open={plateResultModalVisible}
        onCancel={() => {
          setPlateResultModalVisible(false);
          setPlateResult(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setPlateResultModalVisible(false)}>
            İptal
          </Button>,
          <Button key="ok" type="primary" onClick={handlePlateConfirmAndGo}>
            Bilgiler doğru, devam et
          </Button>
        ]}
      >
        {plateResult && (
          <div>
            <p>
              <strong>Plaka:</strong> {plateResult.plate}
            </p>
            <p>
              <UserOutlined /> <strong>En son kayıtlı cari:</strong>{' '}
              {plateResult.customerName}
            </p>
            {(plateResult.brandName || plateResult.make || plateResult.modelName || plateResult.model) && (
              <p>
                <CarOutlined />{' '}
                {[plateResult.brandName || plateResult.make, plateResult.modelName || plateResult.model]
                  .filter(Boolean)
                  .join(' ')}
                {plateResult.year ? ` (${plateResult.year})` : ''}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;

